import {
  Popover as HPopover,
  Disclosure as HDisclosure,
  Transition as HTransition,
} from "@headlessui/react"
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { Chip } from "../../common"
import type {
  TMultiSelectProps,
  TMultiSelectOption,
} from "../MultiSelect/index"
import { ChevronDownSvg } from "../../svg/arrow"
import Checkbox from "../Checkbox"
import { PlusCircleIcon } from "@heroicons/react/24/outline"
import Input from "../Input"
import BetaIcon from "../../common/BetaIcon"

export type TMultiSelectWithChipsOption = TMultiSelectOption & {
  group?: string
}

export type TMultiSelectWithChipsOptionGroup = {
  label: string
  value: string
}

type TGroupedOption = TMultiSelectWithChipsOptionGroup & {
  options: TMultiSelectWithChipsOption[]
}

type TMultiSelectWithChipsProps = Omit<TMultiSelectProps, "options"> & {
  disabled?: boolean
  placeholder?: string
  groups?: TMultiSelectWithChipsOptionGroup[]
  options: TMultiSelectWithChipsOption[]
  showTypeAhead?: boolean
}

const NO_GROUP_OPTION_VALUE = "__no-group-multi-select-with-chips-value__"

const noGroupOption: TMultiSelectWithChipsOptionGroup = {
  label: "< No Group >",
  value: NO_GROUP_OPTION_VALUE,
}

const UNKNOWN_GROUP_OPTION_VALUE =
  "__unknown-group-multi-select-with-chips-value__"

const unknownGroupOption: TMultiSelectWithChipsOptionGroup = {
  label: "< Unknown Group >",
  value: UNKNOWN_GROUP_OPTION_VALUE,
}

export default function MultiSelectWithChips({
  options,
  disabled,
  className,
  value: valueProps,
  defaultValue,
  placeholder,
  classes,
  groups,
  showTypeAhead = false,
  onChange: onChangeFromProps,
}: TMultiSelectWithChipsProps) {
  const isControlled = typeof valueProps !== "undefined"
  const hasDefaultValue = typeof defaultValue !== "undefined"

  const [internalValue, setInternalValue] = useState<TMultiSelectOption[]>(
    hasDefaultValue ? defaultValue : [],
  )

  const [typeAhead, setTypeAhead] = useState("")

  const componentValue = isControlled ? valueProps : internalValue

  const onChange = useCallback(
    (e: TMultiSelectOption[]) => {
      if (onChangeFromProps) {
        onChangeFromProps(e)
      }

      if (!isControlled) {
        setInternalValue(e)
      }
    },
    [isControlled, onChangeFromProps],
  )

  const handleRemove = (idx: number) => {
    if (idx > componentValue.length - 1) {
      return
    }
    const mutableArray = Array.isArray(componentValue)
      ? [...componentValue]
      : [componentValue]
    mutableArray.splice(idx, 1)
    onChange(mutableArray)
  }

  const getSelectedIdx = useCallback(
    (option: TMultiSelectOption) =>
      componentValue.findIndex(_option => option.value === _option.value),
    [componentValue],
  )

  const isOptionSelected = (option: TMultiSelectOption) =>
    getSelectedIdx(option) !== -1

  const toggle = useCallback(
    (option: TMultiSelectOption) => {
      if (option.disabled) {
        return
      }
      if (isOptionSelected(option)) {
        const idx = getSelectedIdx(option)
        const newSelected = [...componentValue]
        newSelected.splice(idx, 1)
        onChange(newSelected)
      } else {
        onChange([...componentValue, option])
      }
    },
    [componentValue],
  )

  const handleChangeTypeAhead = (evt: ChangeEvent<HTMLInputElement>) => {
    setTypeAhead(evt.target.value)
  }

  const groupsMap = useMemo(() => {
    if (!groups) {
      null
    }
    return groups?.reduce(
      (acc, group) => {
        if (!acc[group.value]) {
          acc[group.value] = group
        }
        return acc
      },
      {} as Record<string, TMultiSelectWithChipsOptionGroup>,
    )
  }, [groups])

  const filteredOptions = useMemo(
    () =>
      typeAhead
        ? options.filter(option =>
            option.label.toLowerCase().includes(typeAhead.toLowerCase()),
          )
        : options,
    [typeAhead, options],
  )

  const filteredAvailableOptions = useMemo(() => {
    const usedValues = (componentValue ?? []).map(({ value }) => value)
    return filteredOptions.filter(option => !usedValues.includes(option.value))
  }, [componentValue, filteredOptions])

  const potentialOption = useMemo(() => {
    if (typeAhead && filteredAvailableOptions) {
      return filteredAvailableOptions[0]
    }
    return null
  }, [componentValue, typeAhead])

  const handleKeyUp = (evt: KeyboardEvent) => {
    if (evt.key === "Enter" && potentialOption) {
      toggle(potentialOption)
      setTypeAhead("")
    }
  }

  const grouppedOptions = useMemo(() => {
    if (!groups) {
      return null
    }

    const groupsWithOptions = {} as Record<string, TGroupedOption>

    filteredOptions.forEach(option => {
      let group = noGroupOption
      if (option.group) {
        if (groupsMap?.[option.group]) {
          group = groupsMap[option.group]
        } else {
          group = unknownGroupOption
        }
      }
      if (!groupsWithOptions[group.value]) {
        const groupingOption: TGroupedOption = {
          ...group,
          options: [] as TMultiSelectWithChipsOption[],
        }
        groupsWithOptions[group.value] = groupingOption
      }
      groupsWithOptions[group.value].options.push(option)
    })
    return Object.values(groupsWithOptions)
  }, [options, groups, typeAhead])

  const renderOptions = (options: TMultiSelectOption[]) =>
    options.map((option, idx) => {
      const isFirst = idx === 0
      const isLast = options.length - 1 === idx
      return (
        <div
          key={option.value}
          className={cn(
            `px-system-xs py-system-xxs text-sm cursor-pointer flex items-center space-x-system-xs w-full pr-system-lg hover:bg-system-blue-500 ${option.disabled ? "disabled" : ""}`,
            isFirst && "rounded-t-md",
            isLast && "rounded-b-md",
          )}
          onClick={() => toggle(option)}
          aria-label={option.label}
          aria-selected={isOptionSelected(option)}
        >
          <Checkbox
            checked={isOptionSelected(option)}
            className="checked:bg-brand"
            onChange={() => toggle(option)}
            onClick={(e: MouseEvent) => {
              e.stopPropagation()
            }}
          />
          <div
            className={cn(
              `${!isOptionSelected(option) ? "ms-6" : ""}`,
              "whitespace-nowrap flex items-center justify-between w-full gap-system-xxs pl-system-xxs",
            )}
          >
            <div>
              {option.icon && (
                <div className="w-system-xxs h-system-xxs flex items-center justify-start">
                  {option.icon}
                </div>
              )}
              {option.label}
            </div>
            {option?.isBeta && (
              <div className="w-system-xxs h-system-xxs flex items-center justify-start">
                <BetaIcon />
              </div>
            )}
          </div>
        </div>
      )
    })

  return (
    <HPopover
      className={`relative ${className} ring-0 focus:ring-0 space-y-system-xs w-full`}
    >
      {({ open }) => (
        <>
          <HPopover.Button
            className={cn(
              "flex items-center w-full min-h-system-2xl ring-0 focus:ring-0 outline-none justify-between gap-system-xs px-system-xs py-system-xs text-sm text-white rounded-system-md bg-system-blue-800",
              className,
            )}
          >
            <div className="flex items-center justify-start gap-system-xs flex-wrap">
              {(componentValue ?? []).length === 0 &&
                !potentialOption &&
                (placeholder ?? "Please select option")}
              {componentValue.length > 0 &&
                componentValue.map((option, idx) => (
                  <Chip
                    onRemove={(e: MouseEvent) => {
                      handleRemove(idx)
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    key={idx}
                    className={`border-none bg-system-blue-600 px-system-xs py-system-xxs leading-4`}
                    style={
                      option.color
                        ? {
                            border: `solid 2px ${option.color}`,
                            background: `${option.color}33`,
                          }
                        : undefined
                    }
                  >
                    {option.label}
                  </Chip>
                ))}
              {potentialOption && open && (
                <Chip
                  onRemove={(e: MouseEvent) => {
                    setTypeAhead("")
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  className={`border-none bg-system-blue-600 px-system-xs py-system-xxs leading-4`}
                  style={
                    potentialOption.color
                      ? {
                          border: `dashed 2px ${potentialOption.color}`,
                          background: `${potentialOption.color}33`,
                        }
                      : undefined
                  }
                >
                  {potentialOption.label}
                </Chip>
              )}
              <PlusCircleIcon className="w-6.5 h-6.5" />
            </div>
          </HPopover.Button>
          {!disabled && (
            <HPopover.Panel className="absolute z-40 w-full">
              <div
                className={cn(
                  "max-h-75 overflow-y-auto scrollbar container w-full text-white border rounded-md border-basic-blue bg-system-blue-800",
                  classes?.list,
                )}
              >
                {showTypeAhead && (
                  <div className="px-system-xs py-system-xxs w-full">
                    <Input
                      className="border-none bg-basic-black"
                      fullWidth
                      autoFocus
                      value={typeAhead}
                      placeholder="Search"
                      onChange={handleChangeTypeAhead}
                      onKeyUp={handleKeyUp}
                    />
                  </div>
                )}
                {(!groups || !grouppedOptions) && renderOptions(options)}
                {groups && grouppedOptions && (
                  <div className="flex flex-col min-w-100 px-system-xs overflow-hidden py-system-xs">
                    {grouppedOptions.map((groupOption, idx) => (
                      <HDisclosure key={`${idx}-${groupOption.value}`}>
                        {({ open }) => (
                          <>
                            <HDisclosure.Button
                              className={cn(
                                "group py-system-xs text-start flex items-center justify-start gap-system-xs w-full leading-5",
                                {
                                  "border-t border-system-blue-500": idx > 0,
                                },
                              )}
                            >
                              <ChevronDownSvg
                                className={cn("w-5", {
                                  "rotate-180": !open && !typeAhead,
                                })}
                              />
                              <div className="flex items-end gap-system-xxs">
                                <span>{groupOption.label}</span>
                                <span className="text-sm">
                                  (
                                  {
                                    groupOption.options.filter(option =>
                                      isOptionSelected(option),
                                    ).length
                                  }{" "}
                                  of {groupOption.options.length} selected )
                                </span>
                              </div>
                            </HDisclosure.Button>
                            <HTransition
                              show={open || !!typeAhead}
                              as="div"
                              className="text-gray-500 transition duration-200 ease-out data-[closed]:opacity-0"
                              enterFrom="transform scale-95 opacity-0"
                              enterTo="transform scale-100 opacity-100"
                              leaveFrom="transform scale-100 opacity-100"
                              leaveTo="transform scale-95 opacity-0"
                            >
                              <HDisclosure.Panel>
                                {renderOptions(groupOption.options)}
                              </HDisclosure.Panel>
                            </HTransition>
                          </>
                        )}
                      </HDisclosure>
                    ))}
                  </div>
                )}
              </div>
            </HPopover.Panel>
          )}
        </>
      )}
    </HPopover>
  )
}
